/**
 * The platform admin's overview: how the platform is used, and whether it is
 * healthy. Operator-only, like the rest of `platform.ts`.
 *
 * **Takings come one series per currency and are never added together** —
 * a restaurant trades in its own money. Show each with `formatMoney(amount,
 * currency)`; there is no platform total, on purpose.
 *
 * Both are asked for again every minute while the tab is in view (React
 * Query pauses the interval in a hidden tab).
 */

import { useQuery } from '@tanstack/react-query';

import { useApi } from './provider';

/** How often the overview asks again while it is on screen. */
export const OVERVIEW_REFRESH_MS = 60_000;

export interface UsageCounts {
  total: number;
  active: number;
  suspended: number;
  /** On a free trial now; counted among the active too. */
  on_trial: number;
  /** Opened in the last 30 days. */
  opened_recently: number;
  /** Sites open now, across every restaurant. */
  sites: number;
}

export interface UsageDay {
  /** A trading day, `YYYY-MM-DD`. */
  day: string;
  /** Closed orders, every restaurant and currency together. */
  orders: number;
  /** Restaurants that closed at least one order that day. */
  restaurants: number;
}

export interface UsageTakings {
  currency: string;
  /** Over the window, in this currency alone. Decimal string. */
  total: string;
  days: { day: string; amount: string }[];
}

export interface PlatformUsage {
  /** Today on the UTC calendar, and the window's first day. */
  today: string;
  since: string;
  restaurants: UsageCounts;
  /** Every day of the window, oldest first; a quiet day is a zero. */
  days: UsageDay[];
  takings: UsageTakings[];
  /** Most closed orders in the last seven days. */
  busiest_this_week: { id: string; name: string; orders: number }[];
  /** Open restaurants with no order in `quiet_days` days. */
  quiet: { id: string; name: string; opened_at: string; last_order_on: string | null }[];
  quiet_days: number;
}

export type HealthStatus = 'ok' | 'warning' | 'problem';

export interface HealthCheck {
  key: string;
  label: string;
  status: HealthStatus;
  /** What it means, in a sentence. */
  reason: string;
  /** The figure behind it: a size, a commit, a file. */
  detail: string | null;
}

export interface PlatformHealth {
  /** The worst of the checks. */
  status: HealthStatus;
  checked_at: string;
  checks: HealthCheck[];
}

export function usePlatformUsage() {
  const api = useApi();
  return useQuery({
    queryKey: ['platform', 'usage'],
    queryFn: ({ signal }) => api.get<PlatformUsage>('/v1/platform/usage', signal),
    refetchInterval: OVERVIEW_REFRESH_MS,
  });
}

export function usePlatformHealth() {
  const api = useApi();
  return useQuery({
    queryKey: ['platform', 'health'],
    queryFn: ({ signal }) => api.get<PlatformHealth>('/v1/platform/health', signal),
    refetchInterval: OVERVIEW_REFRESH_MS,
  });
}

/* --- The public site's visitors ------------------------------------------- */

export interface WebsiteDay {
  /** A UTC calendar day, `YYYY-MM-DD`. */
  day: string;
  /** About how many people: a masked address and a browser, once a day. */
  visitors: number;
  views: number;
}

export interface WebsitePage {
  path: string;
  views: number;
  visitors: number;
}

export interface WebsiteShare {
  /**
   * A search engine's name, a site's host or "Direct"; "Phone", "Tablet" or
   * "Computer"; or a two-letter language code ("?" when none was sent).
   */
  name: string;
  visitors: number;
}

/** Counted from the site's access log: no script, no cookie (see the API's
 *  `site_stats`). */
export interface PlatformWebsite {
  /** False when this server keeps no log for the site; `reason` says why. */
  available: boolean;
  reason: string | null;
  today: string;
  since: string;
  /** The oldest day the log reaches; zeros before it mean "not logged yet". */
  logged_since: string | null;
  /** Different visitors across the window, each once. */
  visitors: number;
  views: number;
  days: WebsiteDay[];
  pages: WebsitePage[];
  sources: WebsiteShare[];
  devices: WebsiteShare[];
  languages: WebsiteShare[];
}

/** Visitors to the public site over the last `days` days (1 to 90). The
 *  server keeps the answer five minutes, so asking every minute is cheap. */
export function usePlatformWebsite(days: number) {
  const api = useApi();
  return useQuery({
    queryKey: ['platform', 'website', days],
    queryFn: ({ signal }) =>
      api.get<PlatformWebsite>(`/v1/platform/website?days=${days}`, signal),
    refetchInterval: OVERVIEW_REFRESH_MS,
  });
}
